// S6 Imports, /catalogue/imports (spec 01 §4 S6).
//
// The spec makes this screen module 02's shared import component, scoped to
// the catalogue kinds and backed by /v1/imports. That component exists: the
// onboarding catalogue step and the sales import pages already run it. So
// this file is the frame and nothing more. It renders the same upload panel
// and jobs table (onboarding/screens/imports/upload-panel.tsx, jobs-table.tsx)
// on CATALOGUE_IMPORT_KINDS (products, materials, BOMs, routings), and a job
// opens on module 02's job page (/onboarding/imports/$id), which carries the
// mapping, the validation report with its row errors, the duplicate policy,
// the commit and the error report. Until 2026-10-16 the route rendered "This
// screen is being built." over all of that.
//
// WHO. Spec 01 S6 names planner, facility_admin and group_admin; the codes
// behind them are module 02's `onboarding.import.*` lanes, read off the
// onboarding mirror. A reader with none of the catalogue lanes reads the
// forbidden state. While the role feed is in flight the page holds a skeleton.
// A feed that failed is not read as a refusal: the server still gates every
// call, and the upload panel's own mirror stays closed.

import { useTranslation } from "react-i18next";
import { PageHeader } from "../../../../components/breadcrumbs";
import { CardSkeleton, ForbiddenState } from "../../../../components/states";
import { useRoleCodesFeed } from "../../../../lib/role-codes";
import { useOnboardingPermissions } from "../../../onboarding/hooks/permissions";
import {
  CATALOGUE_IMPORT_KINDS,
  type ImportKind,
} from "../../../onboarding/screens/imports/import-model";
import { ImportJobsTable } from "../../../onboarding/screens/imports/jobs-table";
import { ImportUploadPanel } from "../../../onboarding/screens/imports/upload-panel";
import "../../../onboarding/screens/imports/imports.css";

/** Where the reader stands with the catalogue lanes (pure). */
export type CatalogueImportGate = "pending" | "refused" | "open";

export function catalogueImportGate(feed: {
  readonly isResolved: boolean;
  readonly isFeedError: boolean;
  readonly mayImportAny: boolean;
}): CatalogueImportGate {
  if (!feed.isResolved) return "pending";
  if (!feed.isFeedError && !feed.mayImportAny) return "refused";
  return "open";
}

export function ImportsScreen() {
  const { t } = useTranslation();
  const perms = useOnboardingPermissions();
  const feed = useRoleCodesFeed();
  const canImport = (kind: ImportKind): boolean => perms.canImport(kind);
  const gate = catalogueImportGate({
    isResolved: perms.isResolved,
    isFeedError: feed.isFeedError,
    mayImportAny: CATALOGUE_IMPORT_KINDS.some(canImport),
  });

  return (
    <section className="cat-screen catalogue-imports" data-testid="catalogue-imports">
      <PageHeader
        title={t("catalogue.screens.imports")}
        crumbs={[{ label: t("nav.catalogue"), to: "/catalogue/products" }]}
      />
      {gate === "pending" ? (
        <CardSkeleton lines={5} label={t("catalogue.state.loading")} />
      ) : gate === "refused" ? (
        <ForbiddenState
          title={t("catalogue.imports.forbiddenTitle")}
          body={t("catalogue.imports.forbiddenBody")}
        />
      ) : (
        <div className="catalogue-import">
          <ImportUploadPanel kinds={CATALOGUE_IMPORT_KINDS} canImport={canImport} />
          <ImportJobsTable kinds={CATALOGUE_IMPORT_KINDS} canRun={canImport} />
        </div>
      )}
    </section>
  );
}
